import { queryOrThrow } from "@rootreeweb/js-lib";

import type { IAjaxObj } from "../types.js";
import type { Category, DataSource, Item } from "./types.js";
import { overlayColours, serviceImages } from "./types.js";

declare let RTAjax: IAjaxObj;

/** The RT Portfolio controller. */
export default class RTPortfolio {
	/** All the portfolio categories */
	categories: Array<Category>;

	/** The element containing data found on the page from the PHP. */
	dataElement: HTMLDataElement;

	/** The data found on the page from the PHP. */
	dataSource: DataSource;

	/** An array containing the list items for each of the filter buttons. */
	filterButtons: Array<HTMLLIElement>;

	/** The container for holding the rendered portfolio items. */
	itemContainerElem: HTMLDivElement;

	portfolioItems: Array<Item>;

	private readonly showNav: boolean;

	/** Creates an instance of the RT Portfolio controller. */
	constructor() {
		const dataSource = queryOrThrow<HTMLDataElement>(
			"#rt-portfolio-data-source",
		);

		this.dataSource = dataSource.dataset as DataSource;
		this.portfolioItems = JSON.parse(dataSource.value);
		this.portfolioItems.forEach((item) => {
			item.post_categories = this.bringToFront(
				item.post_categories,
				"featured",
				"slug",
			);
		});
		this.categories = JSON.parse(this.dataSource.categories);
		this.categories = this.bringToFront(this.categories, "featured", "slug");
		this.showNav = this.dataSource.nav === "true";
		this.dataElement = document.getElementById(
			"rt-portfolio-data-source",
		) as HTMLDataElement;

		const itemContainerElem =
			document.querySelector<HTMLDivElement>("#rt-portfolio");
		if (!itemContainerElem) {
			throw new Error("Portfolio container not found");
		}
		this.itemContainerElem = itemContainerElem;
		this.filterButtons = [];

		if (this.showNav) {
			//Add the generated filter buttons to the page
			const portfolioFilters = document.getElementById("rt-portfolio-filters");
			if (portfolioFilters) {
				portfolioFilters.appendChild(this.createFilterButtons());
			}
		}

		//Perform initial render of items
		this.renderItems(this.portfolioItems);
	}

	/**
	 * Brings the item with the provided value to the front of the array.
	 *
	 * @param source The array to sort.
	 * @param value The value to bring to the front.
	 * @param property The property to check for the value.
	 * @returns The sorted array.
	 */
	bringToFront<T>(source: Array<T>, value: string, property?: keyof T): T[] {
		if (property) {
			return source.sort((a, b) => {
				if (a[property] === value) return -1;
				if (b[property] === value) return 1;
				return 0;
			});
		}
		return source.sort((a, b) => {
			if (a === value) return -1;
			if (b === value) return 1;
			return 0;
		});
	}

	/** Remove all items from the container */
	clearItems(): void {
		while (this.itemContainerElem.firstChild) {
			this.itemContainerElem.removeChild(this.itemContainerElem.firstChild);
		}
	}

	/**
	 * @param category Creates the filter button for the provided category.
	 * @returns A List item with the button inside.
	 */
	createFilterButton(category: Category): HTMLLIElement {
		const name = RTAjax.translations[category.slug] || category.name;
		const newItem = document.createElement("li");
		const newButton = document.createElement("button");
		const text = document.createTextNode(name);
		const image = document.createElement("div");
		image.className = "rt-portfolio-badge-container";
		image.innerHTML = serviceImages[category.slug];
		newButton.classList.add("rt-portfolio-button");
		newButton.appendChild(image);
		newButton.appendChild(text);
		newButton.dataset.slug = category.slug;
		newButton.addEventListener("click", (e) => {
			this.toggleFilter(e);
		});
		newItem.appendChild(newButton);
		return newItem;
	}

	/**
	 * Creates the buttons for each category.slug
	 *
	 * @returns A UL with the buttons inside.
	 */
	createFilterButtons(): HTMLUListElement {
		/** List to hold the filter buttons; */
		const buttonList = document.createElement("ul");

		/** Container for the reset button */
		const newItem = this.createResetButton();

		buttonList.appendChild(newItem);

		//Add each category as a button.
		this.categories.forEach((category: Category) => {
			this.filterButtons.push(this.createFilterButton(category));
		});

		this.filterButtons.forEach((button) => {
			buttonList.appendChild(button);
		});
		return buttonList;
	}

	/** Filters the items on the page using the active categories. */
	filterContent(): void {
		/** Categories that currently have an active status. */
		const activeCategories = this.categories
			.filter((category) => category.active)
			.map((category) => category.slug);

		//Show all items if there are no selected categories.
		if (!activeCategories.length) {
			this.renderItems(this.portfolioItems);
			return;
		}

		/** Items that have one or more active categories associated with them. */
		const filteredList = this.portfolioItems.filter((item) => {
			/** An array of the category slugs associated with the post. */
			const currentPostCategories = this.getPostCategories(item);

			return this.getMatchingCategoriesCount(
				currentPostCategories,
				activeCategories,
			);
		});

		this.renderItems(filteredList);
	}

	/**
	 * Generates a string with additional classes for the item container
	 * representing the categories of the item.
	 *
	 * @param item The item to generate the classes for.
	 * @returns A string with additional classes for the item container
	 *   representing the categories of the item.
	 */
	generateClassList(item: Item): string {
		let classList = "";
		item.post_categories.forEach(
			(category) => (classList += " " + category.slug),
		);
		return classList;
	}

	/**
	 * Renders the provided items into the container.
	 *
	 * @param items The items to render.
	 */
	renderItems(items: Array<Item>): void {
		this.clearItems();

		// loop through each item:
		items.forEach((item) => {
			const itemContainerDiv = this.createItem(item);
			// 6. append the newly created div and text to the container node
			this.itemContainerElem.appendChild(itemContainerDiv);
		});

		// select all post in rt-portfolio
		this.createOverlays();
	}

	/** Turns all active filters off and then updates content. */
	resetFilters(): void {
		this.categories.forEach((category) => (category.active = false));
		this.filterButtons.forEach((button) => {
			button.classList.remove("active");
		});

		this.filterContent();
	}

	/**
	 * Handles the onclick for the filter buttons.
	 *
	 * @param e The event args.
	 */
	toggleFilter(e: Event): void {
		const target = e.currentTarget as HTMLElement;
		const slug = target.dataset.slug;
		const activeCategory = this.categories.find(
			(category) => category.slug === slug,
		);

		if (!activeCategory) return;

		activeCategory.active = !activeCategory.active;

		const classList = target.parentElement?.classList;
		if (classList) {
			if (activeCategory.active) classList.add("active");
			else classList.remove("active");
		}

		this.filterContent();
	}

	/**
	 * Creates the image badge for the category.
	 *
	 * @param category The category to create the badge for.
	 * @returns The created list item element.
	 */
	private createImageBadge(category: Category) {
		const listItemElement = document.createElement("li");
		listItemElement.className = "rt-portfolio-badge-list-item";
		const imgElement = document.createElement("div");
		imgElement.classList.add("rt-portfolio-badge");
		imgElement.classList.add(category.slug);
		imgElement.innerHTML = serviceImages[category.slug];
		listItemElement.appendChild(imgElement);
		return listItemElement;
	}

	/**
	 * Create a single portfolio item to render.
	 *
	 * @param item The item to generate the element for.
	 * @returns The created div element.
	 */
	private createItem(item: Item) {
		const { post_image: image, post_title: title, post_url: url } = item;

		// 1. create a new div element
		const containerElement = document.createElement("div");
		const overlayElement = document.createElement("div");

		// 2. assign class name to the div. ex: grid-item0, grid-item1, grid-item2...etc.
		containerElement.className = "grid-item " + this.generateClassList(item);
		overlayElement.className = "overlay";

		const titleContainer = document.createElement("a");
		titleContainer.className = "rt-portfolio-title-container";
		titleContainer.href = url;
		// 3. create a new p tag
		const titleElement = document.createElement("span");
		titleElement.textContent = title;
		titleElement.className = "rt-portfolio-title";

		// 4. append the text node to the newly created div
		containerElement.appendChild(overlayElement); // Overlay should be in the dom first so it is under the text/content.
		containerElement.appendChild(titleContainer);
		titleContainer.appendChild(titleElement);

		if (this.categories.length > 1) {
			const badgeContainer = this.createItemBadges(item);
			containerElement.appendChild(badgeContainer);
		}

		// 5. check if there is an image in post_image
		if (image) {
			// assign post_image to grid-item
			containerElement.style.backgroundImage = `url(${image})`;
		}
		return containerElement;
	}

	/**
	 * Creates the category badges for the item.
	 *
	 * @param item The item to create the badges for.
	 * @returns The created list element.
	 */
	private createItemBadges(item: Item) {
		const listElement = document.createElement("ul");
		listElement.className = "rt-portfolio-badge-list";
		item.post_categories.forEach((category) => {
			const listItem = this.createImageBadge(category);
			listElement.appendChild(listItem);
		});
		return listElement;
	}

	/** Creates the coloured overlays over the portfolio items. */
	private createOverlays() {
		const overlays = document.querySelectorAll<HTMLDivElement>(".overlay");

		overlays.forEach((overlay) => {
			overlay.style.backgroundColor =
				overlayColours[Math.floor(Math.random() * overlayColours.length)];
		});
	}

	/**
	 * Creates the reset button for the filters.
	 *
	 * @returns A list item containing the reset button.
	 */
	private createResetButton() {
		/** Reset button container */
		const newItem = document.createElement("li");

		/** Reset button */
		const newButton = document.createElement("button");

		newButton.textContent = RTAjax.translations["show-all"];
		newButton.classList.add("rt-portfolio-button");
		newButton.dataset.slug = "all";
		newItem.appendChild(newButton);
		newButton.addEventListener("click", () => {
			this.resetFilters();
		});
		return newItem;
	}

	/**
	 * Returns the intersection of the current posts categories and the current
	 * active categories.
	 *
	 * @param currentPostCategories The categories associated with the current
	 *   post.
	 * @param activeCategories The currently active categories.
	 * @returns The number of matching categories
	 */
	private getMatchingCategoriesCount(
		currentPostCategories: string[],
		activeCategories: string[],
	) {
		return currentPostCategories.filter((category) =>
			activeCategories.includes(category),
		).length;
	}

	/**
	 * Gets the slugs of the items categories
	 *
	 * @param item The item to get the categories for.
	 * @returns A string array of the category slugs.
	 */
	private getPostCategories(item: Item) {
		return item.post_categories.map((category) => category.slug);
	}
}
